import React, { useEffect, useRef, useState } from "react";
import "../../styles/InlineTask.css";
import EnterIcon from "../../assets/common/enter-arrow-icon.svg";
import SubTaskIcon from "../../assets/action-items/sub-tasks-icon.svg";
import { Tag } from "../../utils/types";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";

interface InlineTaskCreationProps {
  onSave: () => void;
  onClose: () => void;
  placeholder?: string;
  isSubTask?: boolean;
  taskData: {
    title: string;
    assignee: string | null;
    dueDate: string;
    priority: string;
    status: string;
    tags: Tag[];
    soloCoach: string | null;
    execSponsor: string| null;
    client: {
      orgId: string |  null,
      name:string | null,
    },
  };
  setTaskData: React.Dispatch<
    React.SetStateAction<{
      title: string;
      assignee: string;
      dueDate: string;
      priority: string;
      status: string;
      tags: Tag[];
      soloCoach: string;
      execSponsor: string;
      client: {
        orgId: string |  null,
        name:string | null,
      },
    }>
    >;
    middleLeftComponents: React.ReactNode[];
    middleRightComponents: React.ReactNode[];
}
const InlineTaskCreation: React.FC<InlineTaskCreationProps> = ({
  onSave,
  onClose,
  placeholder,
  isSubTask = false,
  taskData,
  setTaskData,
  middleLeftComponents,
  middleRightComponents,
}) => {
  const [showErrors, setShowErrors] = useState(true);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const { showSnackbar } = useDisplaySnackbar();

  const isValid = Boolean(
    taskData.title?.trim() &&
    taskData.assignee &&
    taskData.dueDate &&
    taskData.status
  );

  useEffect(() => {
      // Focus when the row appears
       titleInputRef.current?.focus();
  }, []);

  const handleSaveClick = () => {
    if (!isValid) {
      showSnackbar("info", "Please fill all the required fields before saving.")
      setShowErrors(true);   // ✅ Highlight missing fields
      return;
    }
    onSave();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSaveClick();
      }
    };
  
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSaveClick, isValid, taskData]);
  
  return (
    <div className={`inline-task-row${isSubTask ? " inline-task-row--subtask" : ""}`}>
      <div
        className={`inline-task-left${isSubTask ? " task-tree-title-cell task-tree-title-cell--child" : ""}`}
      >
        {isSubTask && (
          <div className="action-item-icons">
            <div className="task-tree-gutter" />
            <img
              src={SubTaskIcon}
              alt="subtask"
              className="action-item-subtask-icon"
            />
          </div>
        )}
        <input
          ref={titleInputRef}
          className={`inline-task-input ${showErrors && !taskData.title?.trim() ? "error" : ""}`}
          placeholder={placeholder || "Task Name/Title"}
          type="text"
          value={taskData.title}
          onChange={(e) =>
            setTaskData((prev: any) => ({ ...prev, title: e.target.value }))
          }
        />
      </div>

      {/* MIDDLE SCROLLABLE CHIP SECTION */}
      <div className="inline-task-middle">
        <div className="inline-task-middle-scroll">

          {/*  Render Left Side Components */}
          {middleLeftComponents?.map((Comp: any, index) => {
          const key = Comp?.key as string;

          let error = false;
          if (showErrors) {
            if (key === "assignee" && !taskData.assignee) error = true;
            if (key === "due-date" && !taskData.dueDate) error = true;
            if (key === "status" && !taskData.status) error = true;
          }

          // ✅ Clone element and merge className
          const updatedComp = React.cloneElement(Comp, {
            className: `${Comp.props.className ?? ""} ${error ? "error-border" : ""}`.trim()
          });

          return <React.Fragment key={index}>{updatedComp}</React.Fragment>;
        })}


          {/* Divider */}
          <div
            style={{
              width: "1px",
              height: "18px",
              background: "#C2C2CA",
              margin: "0 4px",
            }}
          />

          {/*  Render Right Side Components */}
          {middleRightComponents?.map((Comp, index) => (
            <React.Fragment key={index}>
              {Comp}
              </React.Fragment>
          ))}

        </div>
      </div>

      {/* RIGHT FIXED ACTION BUTTONS */}
      <div className="inline-task-right">
        <button className="inline-btn inline-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className={`inline-btn inline-btn-primary ${!isValid ? "disabled" : ""}`}
          onClick={handleSaveClick}
        >
          <span className="inline-btn-text">Save</span>
          <img src={EnterIcon} alt="enter" className="enter-icon" />
        </button>
      </div>
    </div>
  );
};

export default InlineTaskCreation;
